import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { PurchaseOrderApi, POSkuApi, SkuInspectionHistoryItem, AssignChoiceApi } from '../types';
import DatePicker from '../components/DatePicker';
import PurchaseOrderPDF from '../components/PurchaseOrderPDF';
import PeopleHoverCard from '../components/PeopleHoverCard';
import { getAssignedToPeople } from '../utils/poPeople';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const SKU_COLUMN_DEFS: { key: string; label: string }[] = [
  { key: 'sku_number', label: 'SKU Number' },
  { key: 'image', label: 'Image' },
  { key: 'category', label: 'Product Category' },
  { key: 'vendor', label: 'Vendor Code' },
  { key: 'work_order', label: 'Work Order' },
  { key: 'description', label: 'Description' },
  { key: 'assigned_to', label: 'Assigned To' },
  { key: 'inspected_by', label: 'Inspected By' },
  { key: 'inspection_date', label: 'Inspection Date' },
  { key: 'packs', label: 'Packs' },
  { key: 'qty_per_pack', label: 'Qty per Pack' },
  { key: 'total_qty', label: 'Total Qty' },
  { key: 'actions', label: 'Actions' },
];

const SKU_COLUMN_STORAGE_KEY = 'po-details-sku-visible-columns';

const loadSkuVisibleColumns = (): Record<string, boolean> => {
  const defaults: Record<string, boolean> = {};
  SKU_COLUMN_DEFS.forEach(c => { defaults[c.key] = true; });
  try {
    const saved = localStorage.getItem(SKU_COLUMN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      SKU_COLUMN_DEFS.forEach(c => {
        if (typeof parsed[c.key] === 'boolean') defaults[c.key] = parsed[c.key];
      });
    }
  } catch {
    // ignore malformed storage value, fall back to defaults
  }
  return defaults;
};

const PODetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrderApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string; role: string; profileImage: string | null }[]>([]);
  const [isInspectorDropdownOpen, setIsInspectorDropdownOpen] = useState(false);
  const inspectorDropdownRef = useRef<HTMLDivElement>(null);
  const [isInspectionTypeDropdownOpen, setIsInspectionTypeDropdownOpen] = useState(false);
  const inspectionTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [isInspectionStageDropdownOpen, setIsInspectionStageDropdownOpen] = useState(false);
  const inspectionStageDropdownRef = useRef<HTMLDivElement>(null);
  const [assignForm, setAssignForm] = useState({ user_id: '', inspection_type: '', inspection_stage: '', due_date: '', special_instructions: '' });
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignChoice, setAssignChoice] = useState<AssignChoiceApi | null>(null);
  const [assignMode, setAssignMode] = useState<'same' | 'separate' | null>(null);
  const [historySku, setHistorySku] = useState<POSkuApi | null>(null);
  const [history, setHistory] = useState<SkuInspectionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [unassignSku, setUnassignSku] = useState<POSkuApi | null>(null);
  const [unassigning, setUnassigning] = useState(false);
  const [unassignError, setUnassignError] = useState('');
  const [imageModalSku, setImageModalSku] = useState<POSkuApi | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [visibleSkuColumns, setVisibleSkuColumns] = useState<Record<string, boolean>>(loadSkuVisibleColumns);
  const [isSkuColumnsMenuOpen, setIsSkuColumnsMenuOpen] = useState(false);
  const skuColumnsMenuRef = useRef<HTMLDivElement>(null);
  const visibleSkuColumnCount = SKU_COLUMN_DEFS.filter(c => visibleSkuColumns[c.key]).length;

  const toggleSkuColumn = (key: string) => {
    setVisibleSkuColumns(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // Keep at least one column visible
      if (Object.values(next).every(v => !v)) return prev;
      return next;
    });
  };

  // ── PDF generation state ──
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const pdfStatusMessages = [
    'Preparing purchase order data…',
    'Securing SKU details…',
    'Fetching product images…',
    'Compiling inspection statuses…',
    'Calculating totals…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const fetchPO = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) {
        setPo(data.data);
        setSelectedSkus(data.data.skus?.map((s: POSkuApi) => s.id) || []);
      }
    } catch (err) {
      console.error('Failed to fetch PO:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/users`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) setUsers(data.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  useEffect(() => {
    fetchPO();
    fetchUsers();
  }, [id]);

  useEffect(() => {
    if (!generatingPDF) {
      setPdfStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setPdfStatusIndex((i) => (i + 1) % pdfStatusMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inspectorDropdownRef.current && !inspectorDropdownRef.current.contains(event.target as Node)) {
        setIsInspectorDropdownOpen(false);
      }
      if (inspectionTypeDropdownRef.current && !inspectionTypeDropdownRef.current.contains(event.target as Node)) {
        setIsInspectionTypeDropdownOpen(false);
      }
      if (inspectionStageDropdownRef.current && !inspectionStageDropdownRef.current.contains(event.target as Node)) {
        setIsInspectionStageDropdownOpen(false);
      }
      if (skuColumnsMenuRef.current && !skuColumnsMenuRef.current.contains(event.target as Node)) {
        setIsSkuColumnsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SKU_COLUMN_STORAGE_KEY, JSON.stringify(visibleSkuColumns));
    } catch {
      // ignore storage write failures (e.g. private browsing)
    }
  }, [visibleSkuColumns]);

  const nonAdminUsers = users.filter(u => u.role !== 'admin');
  const selectedInspector = nonAdminUsers.find(u => u.id === assignForm.user_id);

  const inspectionTypeOptions = [
    { value: 'final', label: 'Final Inspection' },
  ];
  const selectedInspectionType = inspectionTypeOptions.find(o => o.value === assignForm.inspection_type);

  const inspectionStageOptions = [
    { value: 'Intro', label: 'Intro' },
    { value: 'Dupro', label: 'Dupro' },
    { value: 'Final', label: 'Final' },
    { value: 'Re-Final', label: 'Re-Final' },
  ];
  const selectedInspectionStage = inspectionStageOptions.find(o => o.value === assignForm.inspection_stage);

  const toggleSkuSelection = (skuId: string) => {
    setSelectedSkus(prev =>
      prev.includes(skuId) ? prev.filter(n => n !== skuId) : [...prev, skuId]
    );
  };

  const toggleAllSkus = () => {
    if (!po?.skus) return;
    if (selectedSkus.length === po.skus.length) {
      setSelectedSkus([]);
    } else {
      setSelectedSkus(po.skus.map(s => s.id));
    }
  };

  // `mode` is only sent from the "Already Assigned" popup (inspector already holds SKUs on this PO).
  const submitAssign = async (mode?: 'same' | 'separate') => {
    setAssignError('');
    setAssigning(true);
    setAssignMode(mode ?? null);
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/assign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          sku_ids: selectedSkus,
          user_id: assignForm.user_id,
          inspection_type: assignForm.inspection_type,
          inspection_stage: assignForm.inspection_stage || null,
          due_date: assignForm.due_date || null,
          special_instructions: assignForm.special_instructions || null,
          ...(mode ? { mode } : {}),
        }),
      });
      const data = await response.json();
      if (data.success) {
        setAssignChoice(null);
        setIsAssignModalOpen(false);
        setAssignForm({ user_id: '', inspection_type: '', inspection_stage: '', due_date: '', special_instructions: '' });
        fetchPO();
      } else if ((data.code === 'ASSIGNMENT_EXISTS' || data.code === 'BATCH_STARTED') && data.data) {
        setAssignChoice(data.data);
      } else {
        setAssignChoice(null);
        setAssignError(data.message || 'Failed to assign');
      }
    } catch (err) {
      setAssignChoice(null);
      setAssignError('Failed to assign. Please try again.');
    } finally {
      setAssigning(false);
      setAssignMode(null);
    }
  };

  const handleAssign = (e: React.FormEvent) => {
    e.preventDefault();
    submitAssign();
  };

  const handleEdit = () => {
    navigate(`/purchase-orders/edit/${po?.id}`);
  };

  const handleUnassign = async () => {
    const assignmentId = unassignSku?.assignments?.[0]?.id;
    if (!unassignSku || !assignmentId) return;
    setUnassignError('');
    setUnassigning(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setUnassignSku(null);
        fetchPO();
      } else {
        setUnassignError(data.message || 'Failed to unassign');
      }
    } catch (err) {
      setUnassignError('Failed to unassign. Please try again.');
    } finally {
      setUnassigning(false);
    }
  };

  // Convert any image URL to a base64 data URL so @react-pdf/renderer can embed
  // it without hitting CORS or auth barriers. GCS URLs are routed through the
  // same-origin API proxy; relative URLs are resolved against the API base.
  const toDataUrl = async (url: string | null | undefined): Promise<string> => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });

    let abs = url;
    if (!/^(https?:|blob:)/.test(abs)) abs = `${API_BASE}${abs.startsWith('/') ? '' : '/'}${abs}`;

    const isGcs = abs.includes('storage.googleapis.com');
    const fetchUrl = isGcs
      ? `${API_BASE}/api/v1/proxy-image?url=${encodeURIComponent(abs)}`
      : abs;

    try {
      const res = await fetch(fetchUrl, { mode: 'cors' });
      if (res.ok) return await blobToDataUrl(await res.blob());
      console.warn('[PDF img] fetch failed', res.status, fetchUrl);
    } catch (e) {
      console.warn('[PDF img] fetch threw', e, fetchUrl);
    }
    console.error('[PDF img] could NOT convert, image will likely be missing:', url);
    return abs;
  };

  const handleGeneratePDF = async () => {
    if (!po) return;
    setGeneratingPDF(true);
    try {
      // Deep-clone so we never mutate the displayed state
      const clone: any = JSON.parse(JSON.stringify(po));

      // URL dedup cache
      const cache = new Map<string, string>();
      const resolve = async (url: string | null | undefined): Promise<string> => {
        if (!url) return '';
        if (cache.has(url)) return cache.get(url)!;
        const data = await toDataUrl(url);
        cache.set(url, data);
        return data;
      };

      for (const sku of clone.skus || []) {
        if (sku?.image_url) sku.image_url = await resolve(sku.image_url);
      }

      const blob = await pdf(<PurchaseOrderPDF po={clone} visibleSkuColumns={visibleSkuColumns} />).toBlob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `PO-${po.po_number.replace(/[^a-zA-Z0-9]+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (err: any) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const openHistory = async (sku: POSkuApi) => {
    setHistorySku(sku);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/skus/${sku.id}/inspection-history`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) setHistory(data.data);
    } catch (err) {
      console.error('Failed to fetch inspection history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openImageModal = (sku: POSkuApi) => {
    setImageModalSku(sku);
    setImageFile(null);
    setImagePreview(sku.image_url || null);
    setImageError('');
  };

  const closeImageModal = () => {
    setImageModalSku(null);
    setImageFile(null);
    setImagePreview(null);
    setImageError('');
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageError('');
  };

  const updateSkuImageInState = (skuId: string, imageUrl: string | null) => {
    setPo(prev => {
      if (!prev || !prev.skus) return prev;
      return { ...prev, skus: prev.skus.map(s => s.id === skuId ? { ...s, image_url: imageUrl } : s) };
    });
  };

  const handleImageUpload = async () => {
    if (!imageModalSku || !imageFile) return;
    setImageUploading(true);
    setImageError('');
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/skus/${imageModalSku.id}/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        updateSkuImageInState(imageModalSku.id, data.data.image_url);
        closeImageModal();
      } else {
        setImageError(data.message || 'Failed to upload image');
      }
    } catch (err) {
      setImageError('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageRemove = async () => {
    if (!imageModalSku) return;
    setImageUploading(true);
    setImageError('');
    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}/skus/${imageModalSku.id}/image`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        updateSkuImageInState(imageModalSku.id, null);
        closeImageModal();
      } else {
        setImageError(data.message || 'Failed to remove image');
      }
    } catch (err) {
      setImageError('Failed to remove image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-indigo-100 text-indigo-700',
      'bg-pink-100 text-pink-700',
      'bg-blue-100 text-blue-700',
      'bg-purple-100 text-purple-700',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // Separate PO version of the SKU's final assignment (shown in the app as PO123-V2).
  const getSkuVersion = (sku: POSkuApi) =>
    sku.assignments?.find(a => a.inspection_type === 'final')?.assignment_version ?? 1;

  const getSkuAssignedTo = (skuId: string) => {
    const skuData = po?.skus?.find(s => s.id === skuId);
    if (!skuData?.assignments || skuData.assignments.length === 0) return 'Unassigned';
    return skuData.assignments.map(a => a.inspector?.name).filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="material-symbols-outlined animate-spin text-[24px] text-slate-400">progress_activity</span>
        <span className="ml-2 text-slate-500">Loading...</span>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <span className="material-symbols-outlined text-[48px] mb-4">error_outline</span>
        <p>Purchase order not found.</p>
        <Link to="/purchase-orders" className="mt-4 text-[#136bce] hover:underline">Back to Purchase Orders</Link>
      </div>
    );
  }

  const inspectionStatuses = po.inspection_statuses || {};

  return (
    <div className="mx-auto w-full max-w-full">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex mb-6 text-sm text-slate-500">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/purchase-orders" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Purchase Orders</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">{po.po_number}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header section */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Order Details</h1>
          <p className="text-sm text-slate-500 mt-1">View details, SKUs and assign inspectors for {po.po_number}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">edit</span>
            Edit PO
          </button>
          <button
            onClick={handleGeneratePDF}
            disabled={generatingPDF}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">
              {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
            </span>
            {generatingPDF ? 'Generating PDF…' : 'Export PDF'}
          </button>
          <button
            onClick={() => setIsAssignModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">person_add</span>
            Assign PO
          </button>
        </div>
      </div>

      {/* Basic Details Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-8">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-slate-400">info</span>
          <h2 className="text-base font-semibold text-slate-900">Basic Details</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchase Order Number</span>
            <span className="text-lg font-bold text-slate-900">{po.po_number}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Work Order</span>
            <span className="text-lg font-bold text-slate-900">{po.work_order || '-'}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Name</span>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center text-slate-600 font-bold text-xs">
                {getInitials(po.customer_name)}
              </div>
              <span className="text-base font-medium text-slate-900">{po.customer_name}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</span>
            <div className="mt-1">
              <PeopleHoverCard title="Assigned To" people={getAssignedToPeople(po)} emptyLabel="Unassigned" showTicks />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ship Date</span>
            <div className="flex items-center gap-2 text-slate-700 font-medium">
              <span className="material-symbols-outlined text-[18px] text-slate-400">event</span>
              {formatDate(po.ship_date)}
            </div>
          </div>
        </div>
      </div>

      {/* Inspection Statuses Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-8">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-slate-400">fact_check</span>
          <h2 className="text-base font-semibold text-slate-900">Inspection Status</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { key: 'pilot', label: 'Pilot Inspection' },
              { key: 'fabric', label: 'Fabric Inspection' },
              { key: 'line', label: 'Line Inspection' },
              { key: 'final', label: 'Final Inspection' },
              { key: 'container', label: 'Container Inspection' }
            ].map((item) => {
              const status = inspectionStatuses[item.key] || 'Pending';
              return (
                <div key={item.key} className="flex flex-col p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <div className={`h-2.5 w-2.5 rounded-full ${
                      status === 'Completed' || status === 'Passed' ? 'bg-emerald-500' :
                      status === 'In Progress' ? 'bg-amber-500 animate-pulse' :
                      status === 'Scheduled' ? 'bg-blue-500' :
                      status === 'Failed' ? 'bg-red-500' :
                      'bg-slate-300'
                    }`} />
                    <span className={`text-sm font-semibold ${
                      status === 'Completed' || status === 'Passed' ? 'text-emerald-700' :
                      status === 'In Progress' ? 'text-amber-700' :
                      status === 'Scheduled' ? 'text-blue-700' :
                      status === 'Failed' ? 'text-red-700' :
                      'text-slate-500'
                    }`}>
                      {status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* SKU Details Section */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-slate-400">inventory_2</span>
            <h2 className="text-base font-semibold text-slate-900">SKU Details</h2>
            <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{po.skus?.length || 0} Items</span>
          </div>
          <div className="relative" ref={skuColumnsMenuRef}>
            <button
              onClick={() => setIsSkuColumnsMenuOpen(o => !o)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">view_column</span>
              Columns
              <span className="material-symbols-outlined text-[18px]">expand_more</span>
            </button>
            {isSkuColumnsMenuOpen && (
              <div className="absolute right-0 z-30 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Show Columns</p>
                {SKU_COLUMN_DEFS.map(col => {
                  const checked = !!visibleSkuColumns[col.key];
                  const isLastVisible = checked && visibleSkuColumnCount === 1;
                  return (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 ${isLastVisible ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce]"
                        checked={checked}
                        disabled={isLastVisible}
                        onChange={() => toggleSkuColumn(col.key)}
                      />
                      {col.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-4 px-6 py-3" scope="col">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce] cursor-pointer"
                    checked={po.skus ? selectedSkus.length === po.skus.length : false}
                    onChange={toggleAllSkus}
                  />
                </th>
                {visibleSkuColumns.sku_number && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">SKU Number</th>}
                {visibleSkuColumns.image && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Image</th>}
                {visibleSkuColumns.category && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Product Category</th>}
                {visibleSkuColumns.vendor && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor Code</th>}
                {visibleSkuColumns.work_order && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Work Order</th>}
                {visibleSkuColumns.description && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>}
                {visibleSkuColumns.assigned_to && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</th>}
                {visibleSkuColumns.inspected_by && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspected By</th>}
                {visibleSkuColumns.inspection_date && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspection Date</th>}
                {visibleSkuColumns.packs && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Packs</th>}
                {visibleSkuColumns.qty_per_pack && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Qty per Pack</th>}
                {visibleSkuColumns.total_qty && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Total Qty</th>}
                {visibleSkuColumns.actions && <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {po.skus?.map((sku) => (
                <tr key={sku.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce] cursor-pointer"
                      checked={selectedSkus.includes(sku.id)}
                      onChange={() => toggleSkuSelection(sku.id)}
                    />
                  </td>
                  {visibleSkuColumns.sku_number && (
                  <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm font-semibold text-slate-900">{sku.sku_number}</span></td>
                  )}
                  {visibleSkuColumns.image && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sku.image_url ? (
                      <button
                        type="button"
                        onClick={() => setFullScreenImage(sku.image_url!)}
                        className="group relative block h-12 w-12 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-[#136bce] transition-all"
                        title="View image"
                      >
                        <img src={sku.image_url} alt={sku.sku_number} className="h-full w-full object-cover" />
                        <span
                          onClick={(e) => { e.stopPropagation(); openImageModal(sku); }}
                          className="absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center rounded-tl-md bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Replace image"
                        >
                          <span className="material-symbols-outlined text-[12px]">edit</span>
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openImageModal(sku)}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#136bce] hover:text-[#136bce] transition-colors"
                        title="Upload image"
                      >
                        <span className="material-symbols-outlined text-[20px]">add</span>
                      </button>
                    )}
                  </td>
                  )}
                  {visibleSkuColumns.category && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.product_category || '-'}</td>
                  )}
                  {visibleSkuColumns.vendor && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.vendor_code || '-'}</td>
                  )}
                  {visibleSkuColumns.work_order && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.work_order || '-'}</td>
                  )}
                  {visibleSkuColumns.description && (
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 font-medium">{sku.description || '-'}</div>
                    {(sku.color || sku.size) && (
                      <div className="text-xs text-slate-500">
                        {[sku.color && `Color: ${sku.color}`, sku.size && `Size: ${sku.size}`].filter(Boolean).join(' | ')}
                      </div>
                    )}
                  </td>
                  )}
                  {visibleSkuColumns.assigned_to && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <PeopleHoverCard title="Assigned To" people={getAssignedToPeople(sku)} emptyLabel="Unassigned" showTicks />
                      {getSkuVersion(sku) > 1 && (
                        <span
                          className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-[#136bce] ring-1 ring-inset ring-blue-200"
                          title={`Separate PO in the app: ${po.po_number}-V${getSkuVersion(sku)}`}
                        >
                          V{getSkuVersion(sku)}
                        </span>
                      )}
                      {sku.assignments && sku.assignments.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setUnassignSku(sku)}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Unassign"
                        >
                          <span className="material-symbols-outlined text-[16px]">person_remove</span>
                        </button>
                      )}
                    </div>
                  </td>
                  )}
                  {visibleSkuColumns.inspected_by && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sku.last_inspection ? (
                      <span className="text-sm font-medium text-slate-700">{sku.last_inspection.inspector_name || '-'}</span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">-</span>
                    )}
                  </td>
                  )}
                  {visibleSkuColumns.inspection_date && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sku.last_inspection ? (
                      <span className="text-sm text-slate-600">{formatDateTime(sku.last_inspection.completed_at)}</span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">-</span>
                    )}
                  </td>
                  )}
                  {visibleSkuColumns.packs && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {sku.qty_per_pack > 0 ? (sku.total_qty / sku.qty_per_pack).toFixed(2).replace(/\.?0+$/, '') : '-'}
                  </td>
                  )}
                  {visibleSkuColumns.qty_per_pack && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{sku.qty_per_pack}</td>
                  )}
                  {visibleSkuColumns.total_qty && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-sm font-medium text-slate-700">{sku.total_qty}</span>
                  </td>
                  )}
                  {visibleSkuColumns.actions && (
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openHistory(sku)}
                        disabled={!sku.last_inspection}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={sku.last_inspection ? 'Inspection history' : 'No inspections yet'}
                      >
                        <span className="material-symbols-outlined text-[16px]">history</span>
                        History
                      </button>
                      <button
                        onClick={() => sku.last_inspection && navigate(`/final-inspection-report/${sku.last_inspection.inspection_id}?sku=${sku.id}`)}
                        disabled={!sku.last_inspection}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#136bce] px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={sku.last_inspection ? 'View inspection report' : 'No report yet'}
                      >
                        <span className="material-symbols-outlined text-[16px]">description</span>
                        View Report
                      </button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
            {po.skus && po.skus.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td></td>
                  {SKU_COLUMN_DEFS.filter(c => visibleSkuColumns[c.key]).map(col => {
                    if (col.key === 'packs') {
                      return (
                        <td key={col.key} className="px-6 py-3 text-sm font-bold text-slate-700">
                          Total: {po.skus!.reduce((sum, s) => sum + (s.qty_per_pack > 0 ? s.total_qty / s.qty_per_pack : 0), 0).toFixed(2).replace(/\.?0+$/, '')} Packs
                        </td>
                      );
                    }
                    if (col.key === 'total_qty') {
                      return (
                        <td key={col.key} className="px-6 py-3 text-sm font-bold text-slate-900">
                          {po.skus!.reduce((sum, s) => sum + s.total_qty, 0).toLocaleString()} Units
                        </td>
                      );
                    }
                    return <td key={col.key}></td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 rounded-b-xl flex justify-end">
          <p className="text-xs text-slate-400 italic">Showing all SKUs for this Purchase Order</p>
        </div>
      </div>

      {/* Assign PO Modal */}
      {isAssignModalOpen && (
        <div aria-labelledby="modal-title" aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsAssignModalOpen(false)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setIsAssignModalOpen(false)}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-8 border-b border-slate-100 pb-6">
                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[28px]">assignment_ind</span>
                    </div>
                    <div className="pt-1">
                      <h3 className="text-xl font-bold leading-6 text-slate-900 tracking-tight" id="modal-title">Assign Purchase Order</h3>
                      <p className="text-sm text-slate-500 mt-1.5">Assign an inspector to selected SKUs for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{po.po_number}</span>.</p>
                    </div>
                  </div>
                </div>

                {assignError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {assignError}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleAssign}>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Selected SKUs List */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Selected SKUs ({selectedSkus.length})
                      </label>
                      <div className="flex flex-wrap gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50 max-h-32 overflow-y-auto">
                        {selectedSkus.length > 0 ? (
                          selectedSkus.map(skuId => {
                            const sku = po.skus?.find(s => s.id === skuId);
                            return (
                              <span key={skuId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold ring-1 ring-blue-200">
                                {sku?.sku_number || skuId}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-400 italic">No SKUs selected</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        QA Inspector <span className="text-red-500">*</span>
                      </label>
                      {/* Hidden input for form validation */}
                      <input type="hidden" required value={assignForm.user_id} />
                      <div className="relative" ref={inspectorDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsInspectorDropdownOpen(prev => !prev)}
                          className="flex items-center gap-2 w-full rounded-xl border-slate-200 bg-white py-3 pl-3.5 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50/50 sm:text-sm sm:leading-6 transition-all cursor-pointer text-left"
                        >
                          {selectedInspector ? (
                            selectedInspector.profileImage ? (
                              <img src={selectedInspector.profileImage} alt={selectedInspector.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                            ) : (
                              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${getAvatarColor(selectedInspector.name)}`}>
                                {getInitials(selectedInspector.name)}
                              </span>
                            )
                          ) : (
                            <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-[14px] text-slate-400">person_search</span>
                            </span>
                          )}
                          <span className={`truncate ${selectedInspector ? 'text-slate-900' : 'text-slate-400'}`}>
                            {selectedInspector ? selectedInspector.name : 'Choose an inspector...'}
                          </span>
                          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400">expand_more</span>
                        </button>
                        {isInspectorDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                            {nonAdminUsers.map(u => (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => { setAssignForm(f => ({ ...f, user_id: u.id })); setIsInspectorDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${assignForm.user_id === u.id ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                              >
                                {u.profileImage ? (
                                  <img src={u.profileImage} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                                ) : (
                                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${getAvatarColor(u.name)}`}>
                                    {getInitials(u.name)}
                                  </span>
                                )}
                                {u.name}
                              </button>
                            ))}
                            {nonAdminUsers.length === 0 && (
                              <p className="px-3 py-2 text-sm text-slate-400 italic">No inspectors available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspection-type">
                        Inspection Module <span className="text-red-500">*</span>
                      </label>
                      {/* Hidden input for form validation */}
                      <input type="hidden" required value={assignForm.inspection_type} />
                      <div className="relative" ref={inspectionTypeDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsInspectionTypeDropdownOpen(prev => !prev)}
                          className="flex items-center gap-2 w-full rounded-xl border-slate-200 bg-white py-3 pl-3.5 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50/50 sm:text-sm sm:leading-6 transition-all cursor-pointer text-left"
                        >
                          <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[14px] text-slate-400">fact_check</span>
                          </span>
                          <span className={`truncate ${selectedInspectionType ? 'text-slate-900' : 'text-slate-400'}`}>
                            {selectedInspectionType ? selectedInspectionType.label : 'Select inspection type...'}
                          </span>
                          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400">expand_more</span>
                        </button>
                        {isInspectionTypeDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                            {inspectionTypeOptions.map(o => (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => { setAssignForm(f => ({ ...f, inspection_type: o.value })); setIsInspectionTypeDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${assignForm.inspection_type === o.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                              >
                                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                  <span className="material-symbols-outlined text-[14px] text-slate-400">fact_check</span>
                                </span>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspection-stage">
                        Inspection Type
                      </label>
                      {/* Hidden input for form validation */}
                      <input type="hidden" value={assignForm.inspection_stage} />
                      <div className="relative" ref={inspectionStageDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsInspectionStageDropdownOpen(prev => !prev)}
                          className="flex items-center gap-2 w-full rounded-xl border-slate-200 bg-white py-3 pl-3.5 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50/50 sm:text-sm sm:leading-6 transition-all cursor-pointer text-left"
                        >
                          <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[14px] text-slate-400">rule</span>
                          </span>
                          <span className={`truncate ${selectedInspectionStage ? 'text-slate-900' : 'text-slate-400'}`}>
                            {selectedInspectionStage ? selectedInspectionStage.label : 'Select inspection type...'}
                          </span>
                          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400">expand_more</span>
                        </button>
                        {isInspectionStageDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                            {inspectionStageOptions.map(o => (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => { setAssignForm(f => ({ ...f, inspection_stage: o.value })); setIsInspectionStageDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${assignForm.inspection_stage === o.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                              >
                                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                  <span className="material-symbols-outlined text-[14px] text-slate-400">rule</span>
                                </span>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="due-date">
                        Due Date
                      </label>
                      <DatePicker
                        value={assignForm.due_date}
                        onChange={(val) => setAssignForm(f => ({ ...f, due_date: val }))}
                        placeholder="Select due date"
                        dropUp
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="instructions">
                        Special Instructions
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute top-3 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">notes</span>
                        </div>
                        <textarea
                          className="block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all resize-none hover:bg-slate-50/50"
                          id="instructions"
                          name="instructions"
                          placeholder="Enter specific inspection guidelines or notes..."
                          rows={3}
                          value={assignForm.special_instructions}
                          onChange={(e) => setAssignForm(f => ({ ...f, special_instructions: e.target.value }))}
                        ></textarea>
                      </div>
                    </div>
                    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                      <button
                        className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all"
                        type="button"
                        onClick={() => setIsAssignModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="inline-flex w-full justify-center items-center rounded-xl bg-[#136bce] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-600 sm:w-auto transition-all disabled:opacity-50"
                        type="submit"
                        disabled={assigning || selectedSkus.length === 0}
                      >
                        {assigning ? (
                          <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                        ) : (
                          <span className="material-symbols-outlined text-[20px] mr-2">check_circle</span>
                        )}
                        {assigning ? 'Assigning...' : 'Assign'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Already Assigned (Update Same PO / Assign Separate PO) Popup */}
      {assignChoice && (
        <div aria-labelledby="assign-choice-title" aria-modal="true" className="fixed inset-0 z-[60] overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => (!assigning ? setAssignChoice(null) : undefined)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setAssignChoice(null)}
                disabled={assigning}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ring-1 shadow-sm bg-amber-50 text-amber-600 ring-amber-100">
                    <span className="material-symbols-outlined text-[24px]">assignment_ind</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold leading-6 text-slate-900 tracking-tight" id="assign-choice-title">Already Assigned</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Inspector <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{assignChoice.inspector_name}</span>
                    </p>
                  </div>
                </div>

                <p className="text-sm text-slate-600">
                  You have already assigned SKUs to this user. Do you want to assign a separate PO?
                </p>

                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-x-2">
                    <span className="font-semibold">Update Same PO</span>
                    <span className="material-symbols-outlined text-[16px] text-slate-400">arrow_forward</span>
                    <span>adds to <span className="font-semibold text-slate-900">{assignChoice.target_label}</span></span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2">
                    <span className="font-semibold">Assign Separate PO</span>
                    <span className="material-symbols-outlined text-[16px] text-slate-400">arrow_forward</span>
                    <span>creates <span className="font-semibold text-slate-900">{assignChoice.next_label}</span></span>
                  </div>
                </div>

                {assignChoice.started_skus.length > 0 && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2.5">
                    <span className="material-symbols-outlined text-[18px] mt-0.5 shrink-0">warning</span>
                    <span>
                      Already started - these will be moved and may be inspected twice:{' '}
                      <strong>
                        {assignChoice.started_skus
                          .map(s => (s.inspector_name ? `${s.sku_number} (${s.inspector_name})` : s.sku_number))
                          .join(', ')}
                      </strong>
                    </span>
                  </div>
                )}

                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    onClick={() => submitAssign('same')}
                    disabled={assigning || !assignChoice.update_same_allowed}
                  >
                    {assigning && assignMode === 'same' && (
                      <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                    )}
                    Update Same PO
                  </button>
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-[#136bce] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-600 sm:w-auto transition-all disabled:opacity-50"
                    type="button"
                    onClick={() => submitAssign('separate')}
                    disabled={assigning}
                  >
                    {assigning && assignMode === 'separate' && (
                      <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                    )}
                    Assign Separate PO
                  </button>
                </div>
                {!assignChoice.update_same_allowed && assignChoice.blocked_reason && (
                  <p className="mt-3 text-xs text-slate-500 sm:text-right">
                    Update Same PO is unavailable: {assignChoice.blocked_reason}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Confirmation Modal */}
      {unassignSku && (
        <div aria-labelledby="unassign-modal-title" aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => (!unassigning ? setUnassignSku(null) : undefined)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setUnassignSku(null)}
                disabled={unassigning}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-6 flex items-center gap-4">
                  <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ring-1 shadow-sm ${unassignSku.in_progress_inspection ? 'bg-red-50 text-red-600 ring-red-100' : 'bg-amber-50 text-amber-600 ring-amber-100'}`}>
                    <span className="material-symbols-outlined text-[24px]">
                      {unassignSku.in_progress_inspection ? 'warning' : 'person_remove'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold leading-6 text-slate-900 tracking-tight" id="unassign-modal-title">Unassign Inspector</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      SKU <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{unassignSku.sku_number}</span>
                    </p>
                  </div>
                </div>

                {unassignError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {unassignError}
                  </div>
                )}

                {unassignSku.in_progress_inspection ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2.5">
                    <span className="material-symbols-outlined text-[18px] mt-0.5 shrink-0">report</span>
                    <span>
                      <strong>{unassignSku.in_progress_inspection.inspector_name || 'The inspector'}</strong> has already started this PO on their phone.
                      Unassigning removes the SKU from their PO list, but anything already recorded for it stays in their in-progress inspection until they finish or delete it.
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    Remove <strong>{getSkuAssignedTo(unassignSku.id)}</strong> from this SKU? Data will be lost if the inspection has already started.
                  </p>
                )}

                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all"
                    type="button"
                    onClick={() => setUnassignSku(null)}
                    disabled={unassigning}
                  >
                    Cancel
                  </button>
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 hover:bg-red-700 sm:w-auto transition-all disabled:opacity-50"
                    type="button"
                    onClick={handleUnassign}
                    disabled={unassigning}
                  >
                    {unassigning ? (
                      <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                    ) : (
                      <span className="material-symbols-outlined text-[20px] mr-2">person_remove</span>
                    )}
                    {unassigning ? 'Unassigning...' : 'Unassign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection History Modal */}
      {historySku && (
        <div aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setHistorySku(null)}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setHistorySku(null)}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-6 border-b border-slate-100 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[24px]">history</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold leading-6 text-slate-900 tracking-tight">Inspection History</h3>
                      <p className="text-sm text-slate-500 mt-1">Final inspections for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{historySku.sku_number}</span></p>
                    </div>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="material-symbols-outlined animate-spin text-[24px] text-slate-400">progress_activity</span>
                    <span className="ml-2 text-slate-500">Loading...</span>
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <span className="material-symbols-outlined text-[40px] mb-2">inventory</span>
                    <p className="text-sm">No inspection history yet.</p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspected By</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date &amp; Time</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Report</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {history.map((item, idx) => (
                          <tr key={`${item.inspection_id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">{item.inspector_name || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{formatDateTime(item.date)}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${item.status === 'pass' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'}`}>
                                {item.status === 'pass' ? 'Pass' : 'Fail'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <button
                                onClick={() => navigate(`/final-inspection-report/${item.inspection_id}?sku=${historySku.id}`)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-[#136bce] hover:bg-blue-50 transition-colors"
                                title="View report"
                              >
                                <span className="material-symbols-outlined text-[16px]">description</span>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKU Image Upload Modal */}
      {imageModalSku && (
        <div aria-labelledby="image-modal-title" aria-modal="true" className="fixed inset-0 z-50 overflow-y-auto" role="dialog">
          <div
            aria-hidden="true"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={closeImageModal}
          ></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={closeImageModal}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-6 border-b border-slate-100 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[24px]">image</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold leading-6 text-slate-900 tracking-tight" id="image-modal-title">SKU Image</h3>
                      <p className="text-sm text-slate-500 mt-1">Upload a reference image for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{imageModalSku.sku_number}</span></p>
                    </div>
                  </div>
                </div>

                {imageError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {imageError}
                  </div>
                )}

                <div className="flex flex-col items-center gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="h-40 w-40 rounded-xl object-cover border border-slate-200" />
                  ) : (
                    <div className="h-40 w-40 rounded-xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                      <span className="material-symbols-outlined text-[40px]">image</span>
                    </div>
                  )}

                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFileChange} />

                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-[#136bce] hover:text-[#136bce] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">upload</span>
                    {imagePreview ? 'Choose a different image' : 'Choose an image'}
                  </button>
                </div>

                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                  {imageModalSku.image_url && (
                    <button
                      type="button"
                      onClick={handleImageRemove}
                      disabled={imageUploading}
                      className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50 sm:w-auto transition-all disabled:opacity-50"
                    >
                      Remove Image
                    </button>
                  )}
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all"
                    type="button"
                    onClick={closeImageModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-[#136bce] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-600 sm:w-auto transition-all disabled:opacity-50"
                    type="button"
                    onClick={handleImageUpload}
                    disabled={!imageFile || imageUploading}
                  >
                    {imageUploading ? (
                      <span className="material-symbols-outlined animate-spin text-[20px] mr-2">progress_activity</span>
                    ) : (
                      <span className="material-symbols-outlined text-[20px] mr-2">cloud_upload</span>
                    )}
                    {imageUploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen Image Viewer */}
      {fullScreenImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setFullScreenImage(null)}
        >
          <button
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
            onClick={() => setFullScreenImage(null)}
          >
            <span className="material-symbols-outlined text-[28px]">close</span>
          </button>
          <img
            src={fullScreenImage}
            alt="SKU"
            className="max-h-full max-w-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Generating your PDF</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted purchase order report, complete with SKU photos. This usually takes just a few seconds.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p key={pdfStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PODetails;
